import * as angular from "angular"
import { OmniaApiState } from "../api/apiState"
import { HoldingEvent, HoldingSession } from "../api/types"
import { AutoLoginResult, EventNotification, IOmniaHttpService, IOmniaMaintenanceService, IOmnniaSessionState, LoginCredentials, LoginResult, OmniaHttpPostOptions } from "../types"
import { downloadDataAsFile } from "../utils/download"
import { isDate, isFunction, isObject, isString, isUndefinedOrNull } from "../utils/utils"

/** @internal */
export class OmniaHttpService implements IOmniaHttpService {

    private accessToken: string
    private updatePromise: ng.IPromise<any>
    private _updateSession: boolean

    static $inject = ["$http", "$q", "$timeout", "$window", "omniaApiUrl", "OmniaMaintenance", "OmniaApiState", "omniaSessionState"]

    constructor(
        private readonly $http: ng.IHttpService,
        private readonly $q: ng.IQService,
        private readonly $timeout: ng.ITimeoutService,
        $window: ng.IWindowService,
        private readonly omniaApiUrl: string,
        private readonly maintenance: IOmniaMaintenanceService,
        private readonly apiState: OmniaApiState,
        private readonly sessionState: IOmnniaSessionState) {

        setupEventHandlers($window, omniaApiUrl, () => this.accessToken)

    }

    get disabled() {
        return !!(this.maintenance?.current)
    }

    getApiBaseUrl(): string {
        return this.omniaApiUrl
    }


    private clearTimeout() {

        if (this.updatePromise) {
            this.$timeout.cancel(this.updatePromise)
            this.updatePromise = undefined
        }

    }

    private updateOnTimeout() {

        const delay = 60000 * (2 + Math.random())

        this.clearTimeout()

        const pp = this.updatePromise = this.$timeout(() => {

            if (pp == this.updatePromise) {

                this.updatePromise = undefined

                const r = this.updateSessionInternal()

                if (r) {
                    r.finally(() => this.updateOnTimeout())
                }
                else {
                    this.updateOnTimeout()
                }

            }

        }, delay)

    }

    public updateSession(update?: { clientState: any }) {

        this.clearTimeout()
        return this.updateSessionInternal(update).finally(() => this.updateOnTimeout())

    }

    private updateSessionInternal(update?: { clientState: any }) {

        if (this.disabled) return
        if (!this.accessToken) return
        if (!this._updateSession) return

        const url = `/api/session/v1`

        const request = {
            return: {
                sessions: true,
                eventsSince: this.apiState.lastEventDate || new Date(Date.now() - (24 * 60 * 60 * 1000)).toISOString()
            },
            update
        }

        const config = this.getRequestConfig(undefined, false)

        return this.post_(url, request, config, false)
            .then(r => {

                this.sessionState.unauthorized = false

                const { Sessions: sessions, Events: events } = r.data as {
                    Sessions: HoldingSession[]
                    Events: HoldingEvent[]
                }

                this.apiState.updateSessions(sessions || [])
                this.apiState.mergeEvents(events || [])

            })
            .catch((r: ng.IHttpResponse<any>) => {

                if (this.sessionState.handleUnauthorized) {

                    const { status, data } = r || {}

                    if ((status == 401) && isObject(data) && data.OmniaAuthError) {
                        this.sessionState.unauthorized = true
                    }

                }

            })

    }

    login(credentials: LoginCredentials): ng.IPromise<LoginResult> {

        const { username, password, token } = { ...credentials }

        if (this.disabled) {
            return this.$q.reject()
        }

        const url = this.omniaApiUrl + "/oauth/token"

        const data = getUrlEncodedForm(({
            username,
            password,
            token,
            client_id: "8952c9db-6a18-429b-adc0-33097c312df2",
            grant_type: token ? "token" : "password"
        }))

        const options = <ng.IRequestShortcutConfig>{
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            }
        }

        return this.$http.post(url, data, options).then(
            r => {

                const data: any = r.data ?? {}
                const token = this.accessToken = data.access_token
                return { success: !!token, token, restricted: Boolean(data.restricted) }

            },
            r => ({ success: false, token: null, restricted: null, errorMessage: r?.data?.error_description })
        )


    }

    loginAuto(accessToken: string): ng.IPromise<AutoLoginResult> {

        if (this.disabled) {
            return this.$q.reject()
        }

        const p = this.$http.get(this.omniaApiUrl + "/api/session/v1/login", this.getHeaders(accessToken))

        return p.then(
            r => {

                this.accessToken = accessToken

                return { success: true, restricted: !!r.data["restricted"] }

            },
            r => null
        )

    }

    logout(): ng.IPromise<any> {
        return this.delete("/api/session/v1/logout").then(() => {
            this.accessToken = null
        })
    }

    selectHolding(holdingId: number, reuseSession: boolean, clientState: any): ng.IPromise<any> {

        if (this.disabled) {
            return this.$q.reject()
        }

        const payload = {
            holdingId,
            reuseSession,
            clientState
        }

        const url = `/api/session/v1/holdings/select`

        return this.post(url, payload).then(r => {

            if (r.data) {

                const token = r.data["Token"]

                if (token) {

                    this.accessToken = token
                    this._updateSession = true

                    this.updateOnTimeout()

                }

                return r.data

            }

        })

    }

    private getRequestConfig(config?: any, block?: boolean, message?: string): any {

        const headers = this.getHeaders()

        config = (config || {})

        if (config.headers) {
            config.headers = angular.extend(headers.headers, config.headers)
        }
        else {
            config.headers = headers.headers
        }

        config._omnia = {
            block: block !== false,
            message,
            attempts: 1,
        }

        return config

    }

    public get<T>(url: string, config?: {}): ng.IHttpPromise<T>
    public get<T>(url: string, block?: boolean | string, ifModifiedSince?: Date): ng.IHttpPromise<T>
    public get<T>(url: string, args?: {} | boolean | string, ifModifiedSince?: Date): ng.IHttpPromise<T> {

        if (this.disabled) {
            return <ng.IHttpPromise<T>>this.$q.reject()
        }

        const config = this.getRequestConfig(
            isObject(args) ? args : undefined,
            args === false ? false : undefined,
            isString(args) ? args : undefined
        )

        if (isDate(ifModifiedSince)) {
            config.headers["If-Modified-Since"] = ifModifiedSince.toUTCString()
        }

        this.updateOnTimeout()

        return this.$http.get<T>(this.omniaApiUrl + url, config)

    }

    public getArrayBuffer(url: string, block?: boolean): ng.IPromise<ArrayBufferLike> {

        const config = this.getRequestConfig({}, block)

        config.transformResponse = undefined
        config.responseType = "arraybuffer"

        this.updateOnTimeout()

        return this.$http.get<ArrayBufferLike>(this.omniaApiUrl + url, config).then(r => r.data)

    }

    public getExternal<T>(url: string, block = false) {

        const config = {}

        if (!block) {

            config["_omnia"] = {
                block: false
            }

        }

        return this.$http.get<T>(url, config).then(r => r.data)

    }

    public post<T>(url: string, data?: any, changeApiVersion?: boolean): ng.IHttpPromise<T>
    public post<T>(url: string, data?: any, options?: OmniaHttpPostOptions): ng.IHttpPromise<T>
    public post<T>(url: string, data?: any, args?: boolean | OmniaHttpPostOptions): ng.IHttpPromise<T> {

        const options = args === true
            ? { changeApiVersion: true }
            : args === false
                ? { changeApiVersion: false }
                : args || { changeApiVersion: true }

        const config: any = {}

        if (options.clearContentType) {
            config.headers = {
                "Content-Type": undefined
            }
        }

        if (options.blockUi === false) {
            config._omnia = {
                block: false
            }
        }

        const changeGlobalVersion = isUndefinedOrNull(options.changeApiVersion)
            ? true
            : options.changeApiVersion

        return this.post_(url, data, config, changeGlobalVersion)

    }

    private post_<T>(url: string, data?: any, config?: any, changeVersion = true): ng.IHttpPromise<T> {

        if (this.disabled) {
            return <ng.IHttpPromise<T>>this.$q.reject()
        }

        config = this.getRequestConfig(config)

        this.updateOnTimeout()

        let p = this.$http.post<T>(this.omniaApiUrl + url, data, config)

        if (changeVersion) {
            p = p.then(r => {
                this.apiState.incrementVersion()
                return r
            })
        }

        return p

    }

    public delete<T>(url: string, data?: any, changeApiVersion?: boolean): ng.IHttpPromise<T> {

        if (this.disabled) {
            return <ng.IHttpPromise<T>>this.$q.reject()
        }

        const config = this.getHeaders()

        if (data) {
            config.data = data
            config.headers["Content-Type"] = "application/json;charset=UTF-8"
        }

        config["_omnia"] = {
            attempt: 1
        }

        this.updateOnTimeout()

        let p = this.$http.delete<T>(this.omniaApiUrl + url, config)

        if (changeApiVersion) {
            p = p.then(r => {
                this.apiState.incrementVersion()
                return r
            })
        }

        return p

    }

    public put<T>(url: string, data?: any, changeApiVersion?: boolean): ng.IHttpPromise<T> {

        if (this.disabled) {
            return <ng.IHttpPromise<T>>this.$q.reject()
        }

        const config = this.getRequestConfig()

        this.updateOnTimeout()

        let p = this.$http.put<T>(this.omniaApiUrl + url, data, config)

        if (changeApiVersion) {
            p = p.then(r => {
                this.apiState.incrementVersion()
                return r
            })
        }

        return p

    }


    private getHeaders(accessToken?: string): ng.IRequestShortcutConfig {

        accessToken = accessToken || this.accessToken

        if (!accessToken) {
            return {}
        }

        return <ng.IRequestShortcutConfig>{
            headers: {
                "Authorization": "Bearer " + accessToken
            }
        }

    }

    public getAndDownload(url: string, fileName: string, contentType: string): ng.IPromise<ng.IHttpResponse<{}>> {

        const options = {
            transformResponse: undefined,
            responseType: "arraybuffer"
        }

        return this.get(url, options).then(r => {

            contentType = contentType || r.headers("content-type")

            if (!fileName) {
                const contentDisposition = r.headers("content-disposition")
                const m = /filename=\"?([^\"]+)\"?/.exec(contentDisposition)
                fileName = m[1]
            }

            if (r.status == 200) {
                downloadDataAsFile(r.data, fileName, contentType)
            }

            return r

        })

    }


    public postAndDownload(url: string, fileName: string, postData: any, contentType: string = "application/json;charset=utf-8;") {

        const options = {
            transformResponse: undefined,
            responseType: "arraybuffer"
        }

        return this.post_(url, postData, options).then(r => {

            downloadDataAsFile(r.data, fileName, contentType)

        })

    }

    sendNotification(notification: EventNotification): PromiseLike<any> {

        const url = "/api/session/v1/notification"

        return this.post_(url, notification, undefined, false)

    }


}

function getUrlEncodedForm(obj: any) {

    const str = []

    for (var p in obj) {
        const value = obj[p]
        if (value !== undefined) {
            str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]))
        }
    }

    return str.join("&")

}


function setupEventHandlers($window: ng.IWindowService, apiBaseUrl: string, tokenProvider: () => string) {

    if (!isFunction(navigator.sendBeacon)) {
        return
    }

    $window.addEventListener("unload", () => {
        send("disconnect")
    })

    const ignoredErrors = new RegExp("^(ResizeObserver)", "i")

    window.addEventListener("error", e => {

        const { error, message, filename, lineno } = e

        if (message && ignoredErrors.test(message)) {
            return
        }

        window["StackTrace"]
            .fromError(e.error)
            .then(frames => {

                console.error("Uncaught error", frames)

                send("error", { message: e.message, frames })

            })
            .catch(handlerError => {

                console.error(handlerError)

                const stack = error?.["stack"]

                const data = {
                    message,
                    filename,
                    lineno,
                    error,
                    stack: isString(stack) ? stack.split('\n') : undefined
                }

                send("error", data)

            })


    })

    function send(route: string, data?: any) {


        const token = tokenProvider()
        if (!token) {
            return
        }

        const payload = new Blob(
            [JSON.stringify({
                token,
                data
            })],
            {
                type: "application/json"
            }
        )

        const url = `${apiBaseUrl}/api/session/v1/${route}`

        navigator.sendBeacon(url, payload)


    }


}
